import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants'

class ButtonVideo {
  constructor (button) {
    this.button = button
    this.label = button.querySelector('.js-button-label')
    this.emitter = GLOBAL_CONSTANTS.UTILS.EMITTER

    const { controlgroup, loops, replayOnly } = this.button.dataset
    this.autoplay = this.button.dataset.autoplay !== 'false'
    this.loops = !(loops === 'false')
    this.replayOnly = !(replayOnly === 'false')
    this.videos = document.querySelectorAll(`[data-controlledby="${controlgroup}"]`)
    this.initialObserver = undefined
    this.inViewObserver = undefined
    this.videosInView = [true]
    this.userState = 'auto'
    this.labelDesc = this.button.dataset.desc ?? 'the decorative animation'
    this.inCarousel = !!this.button.closest('.js-carousels--horizontal')
    this.controlKey = this.button.dataset.controlKey
    this.isLocked = false

    this.init()
  }

  init() {
    this.videosInView[0] = true
    this.videos.forEach((video, index) => {
      video.dataset.videoIndex = index + 1
      this.videosInView[index + 1] = true
    })

    this.button.addEventListener('click', this.onButtonClick.bind(this))

    if (!this.autoplay || this.hasReducedMotion()) {
      this.videos.forEach(video => {
        video.pause()
        video.currentTime = 0
      })
      this.updateButtonState('play')
    } else {
      this.videos.forEach(video => {
        video.onloadeddata = () => {
          this.hidePoster(video)
        }
        if (video.readyState >= 2) {
          this.hidePoster(video)
        }
      })
    }

    this.emitter.on(GLOBAL_CONSTANTS.EVENTS.REDUCED_MOTION_CHANGE, (prefersReducedMotion) => {
      if (prefersReducedMotion) {
        this.videos.forEach(video => {
          video.pause()
          video.currentTime = 0
          this.showPoster(video)
        })
        this.updateButtonState('play')
      }
    })

    this.emitter.on(GLOBAL_CONSTANTS.EVENTS.IS_MOBILE, ({ isMobile, isFirstLoad }) => {
      if (isMobile) {
        this.resetVideoSource('mobile')
      } else if (isFirstLoad !== true) {
        this.resetVideoSource('desktop')
      }
    })

    if (!this.loops) {
      this.videos.forEach(video => {
        video.onended = () => {
          if (this.allEnded()) {
            setTimeout(() => {
              this.updateButtonState('replay')
            }, 350)
          }
        }
      })
    }

    this.initialObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.autoplay && !this.hasReducedMotion()) {
          this.initialObserver.unobserve(entry.target)
          this.playAll()
          if (this.loops) {
            this.setInViewObserver()
          }
        }
      })
    }, {
      rootMargin: this.loops ? '0px' : `0px 0px ${this.inCarousel ? '0px' : '-50%'}`,
      threshold: this.loops ? 0 : 0.75
    })
    this.initialObserver.observe(this.inCarousel ? this.videos[0] : this.button)

    this.registerInViewObserver()

    this.updateButtonState(this.button.dataset.state)

    if (this.controlKey) {
      if (!('videos' in window.unlocked)) {
        window.unlocked.videos = {}
      }

      window.unlocked.videos[this.controlKey] = {
        play: () => {
          if (
            this.videosInView.includes(true) &&
            !this.hasReducedMotion()
          ) {
            this.playAll()
          }
        },
        pause: (showPoster) => {
          this.pauseAll()
          if (showPoster === true) {
            this.videos.forEach(video => this.showPoster(video))
          }
        },
        state: () => this.userState,
        lock: () => {
          this.lock = true
        },
        unlock: () => {
          this.lock = false
        }
      }
    }
  }

  playAll() {
    if (this.lock === true) return

    this.videos.forEach(video => {
      video.play()
      this.hidePoster(video)
    })
    if (this.replayOnly) {
      this.updateButtonState('hidden')
    } else {
      this.updateButtonState('pause')
    }
  }

  pauseAll() {
    this.videos.forEach(video => video.pause())
    this.updateButtonState('play')
  }

  setInViewObserver() {
    this.inViewObserver = new IntersectionObserver(entries => {
      entries.forEach(video => {
        const index = parseInt(video.target.dataset.videoIndex) || 0
        this.videosInView[index] = video.isIntersecting
      })
      if (!this.hasReducedMotion()) {
        const noneVisible = !this.videosInView.reduce((accumulator, currentValue) => {
          return accumulator || currentValue
        })
        const anyVisible = this.videosInView.includes(true)
        if (anyVisible && this.userState !== 'pause') {
          this.playAll()
        } else if (noneVisible) {
          this.pauseAll()
        }
      }
    }, {
      threshold: 0
    })

    this.videos.forEach((video, index) => {
      this.inViewObserver.observe(video)
    })
    this.inViewObserver.observe(this.button)
  }

  showPoster(video) {
    video.closest('.js-has-poster')?.classList.remove('--hide-poster')
  }

  hidePoster(video) {
    video.closest('.js-has-poster')?.classList.add('--hide-poster')
  }

  hasReducedMotion() {
    return document.documentElement.classList.contains('reduced-motion')
  }

  onButtonClick() {
    switch (this.button.dataset.state) {
      case 'play':
      case 'replay':
        this.userState = 'play'
        this.playAll()
        break
      case 'pause':
        this.userState = 'pause'
        this.pauseAll()
        break
    }
  }

  updateButtonState(state) {
    this.button.dataset.state = state
    if (state === 'hidden') {
      this.button.ariaHidden = true
      this.button.tabIndex = -1
      this.label.innerText = ''
    } else {
      this.button.ariaPressed = state === 'pause'
      this.button.ariaHidden = false
      this.button.removeAttribute('tabindex')
      this.label.innerText = `${state} ${this.labelDesc}`
    }
  }

  allEnded() {
    let allVideosEnded = true
    this.videos.forEach(video => {
      if (!video.ended) {
        allVideosEnded = false
      }
    })
    return allVideosEnded
  }

  resetVideoSource(media) {
    this.videos.forEach(video => {
      const mobileSources = video.querySelectorAll('source[data-media="mobile"]')
      if (mobileSources.length <= 0) {
        return
      }

      const desktopSources = video.querySelectorAll('source[data-media="desktop"]')
      if (media === 'mobile') {
        this.setDatasrcToSrc(mobileSources)
        this.setSrcToDatasrc(desktopSources)
      } else if (media === 'desktop') {
        this.setDatasrcToSrc(desktopSources)
        this.setSrcToDatasrc(mobileSources)
      } else {
        return
      }

      video.load()
      if (this.autoplay && !this.hasReducedMotion()) {
        this.videos.forEach(video => {
          video.play()
          this.hidePoster(video)
        })
        if (this.replayOnly) {
          this.updateButtonState('hidden')
        } else {
          this.updateButtonState('pause')
        }
      }
    })
  }

  setDatasrcToSrc(videos) {
    videos.forEach(source => {
      const { src } = source.dataset
      if (!src) {
        return
      }
      source.src = src
      delete source.dataset.src
    })
  }

  setSrcToDatasrc(videos) {
    videos.forEach(source => {
      const src = source.src
      if (!src) {
        return
      }
      source.dataset.src = src
      source.removeAttribute('src')
    })
  }

  registerInViewObserver() {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.setSyncVideo(entry.target)
        }
        observer.unobserve(entry.target)
      })
    })

    this.videos.forEach(video => {
      observer.observe(video)
    })
  }

  setSyncVideo(video) {
    const source = video.querySelector('source[src]')
    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'video'
    link.setAttribute('href', source.src)
    link.setAttribute('type', source.type)
    document.head.appendChild(link)
  }
}

export default {
  selector: '.js-media-button',
  Source: ButtonVideo
}
